import React, { useMemo } from 'react';
import { ExcavationSite } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { PERIOD_COLORS } from '../constants';
import { PieChart as PieIcon, BarChart3 } from 'lucide-react';

interface StatsChartProps {
  data: ExcavationSite[];
}

export const StatsChart: React.FC<StatsChartProps> = ({ data }) => {
  const periodData = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach(site => {
      const p = site.period || 'Belirsiz';
      counts[p] = (counts[p] || 0) + 1;
    });
    return Object.keys(counts)
      .map(key => ({ name: key, value: counts[key] }))
      .sort((a, b) => b.value - a.value); // Sort by count
  }, [data]);

  const locationData = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach(site => {
      const l = site.location || 'Bilinmiyor';
      counts[l] = (counts[l] || 0) + 1;
    });
    // Return top 5 locations
    return Object.keys(counts)
      .map(key => ({ name: key, value: counts[key] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [data]);

  const getColor = (name: string) => {
    const p = (name || '').toLowerCase();
    for (const [key, color] of Object.entries(PERIOD_COLORS)) {
      if (p.includes(key.toLowerCase())) return color;
    }
    return '#78716c';
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur border border-stone-200 p-3 rounded-lg shadow-xl text-sm">
          <p className="font-bold text-stone-800 mb-1">{label || payload[0].name}</p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: payload[0].fill || payload[0].color }}></span>
            <span className="text-stone-600">
              {payload[0].value} Alan
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) return null;

  return (
    <div className="space-y-6 mt-6">
      
      {/* Period Distribution Card */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-200/60 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-sm font-bold text-stone-700 uppercase tracking-wider flex items-center">
            <PieIcon className="w-4 h-4 mr-2 text-amber-600" />
            Dönem Dağılımı
          </h4>
          <span className="text-xs font-medium text-stone-400 bg-stone-100 px-2 py-1 rounded-full">{periodData.length} Dönem</span>
        </div>
        
        <div className="h-64 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={periodData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={85}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
              >
                {periodData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getColor(entry.name)} 
                    className="hover:opacity-80 transition-opacity cursor-pointer"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} cursor={false} />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Center Text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <span className="block text-3xl font-bold text-stone-800">{data.length}</span>
              <span className="text-xs text-stone-500 font-medium uppercase tracking-wide">Toplam Alan</span>
            </div>
          </div>
        </div>
        
        {/* Custom Legend for top items */}
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          {periodData.slice(0, 5).map((entry, idx) => (
            <div key={idx} className="flex items-center gap-1.5 text-xs text-stone-600 bg-stone-50 px-2 py-1 rounded-md border border-stone-100">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getColor(entry.name) }}></div>
              <span className="font-medium truncate max-w-[80px]">{entry.name}</span>
              <span className="text-stone-400 ml-1">({entry.value})</span>
            </div>
          ))}
          {periodData.length > 5 && (
            <div className="text-xs text-stone-400 py-1 px-2">+ {periodData.length - 5} diğer</div>
          )}
        </div>
      </div>

      {/* Location Distribution Card */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-200/60 hover:shadow-md transition-shadow">
        <h4 className="text-sm font-bold text-stone-700 uppercase tracking-wider mb-6 flex items-center">
          <BarChart3 className="w-4 h-4 mr-2 text-amber-600" />
          Bölgesel Yoğunluk (İlk 5)
        </h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={locationData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e7e5e4" />
              <XAxis type="number" hide />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={100} 
                tick={{ fontSize: 11, fill: '#57534e', fontWeight: 500 }} 
                axisLine={false}
                tickLine={false}
              />
              <Tooltip 
                cursor={{fill: '#f5f5f4', radius: 4}} 
                content={<CustomTooltip />}
              />
              <Bar 
                dataKey="value" 
                fill="#d97706" 
                radius={[0, 6, 6, 0]} 
                barSize={24}
                className="hover:opacity-90 transition-opacity"
              >
                 {locationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index === 0 ? '#b45309' : '#d97706'} />
                 ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};